import { useState, useEffect, useCallback } from "react";
import { StickyNote, Plus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import API from "@/lib/api";

/**
 * Internal staff notes attached to a single order. Pulls from
 * GET/POST /api/orders/{order_id}/staff-notes. Visible to all staff.
 */
export default function OrderNotesPanel({ orderId, className = "" }) {
  const [notes, setNotes] = useState(null);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!orderId) return;
    API.get(`/orders/${encodeURIComponent(orderId)}/staff-notes`)
      .then(({ data }) => setNotes(Array.isArray(data) ? data : []))
      .catch(() => setNotes([]));
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!body.trim()) { toast.info("Type a note first."); return; }
    setSaving(true);
    try {
      await API.post(`/orders/${encodeURIComponent(orderId)}/staff-notes`, { body: body.trim() });
      setBody("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save note");
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <section className={`bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl p-4 ${className}`} data-testid="order-notes-panel">
      <div className="flex items-center gap-2 mb-3">
        <StickyNote className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Internal notes</h3>
        {notes !== null && <span className="text-xs text-slate-400">({notes.length})</span>}
        <span className="ml-auto text-[11px] text-slate-400">Order-only · all staff</span>
      </div>

      <div className="space-y-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="e.g. Held for ID verification — patient sending updated DL via secure upload"
          rows={2}
          maxLength={5000}
          className="rounded-xl text-sm"
          data-testid="order-note-input"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={add} disabled={saving || !body.trim()} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="order-note-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            Add Note
          </Button>
        </div>
      </div>

      {notes === null ? (
        <div className="mt-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl h-10 animate-pulse" />
      ) : notes.length === 0 ? (
        <p className="text-xs text-slate-400 italic mt-3">No notes on this order yet.</p>
      ) : (
        <ul className="mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="order-notes-list">
          {notes.map((n) => (
            <li key={n.id} className="bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/40 rounded-lg p-2.5 text-xs">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{n.staff_name || "Staff"}</span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-white border border-amber-100 rounded-full px-2 py-0.5">{n.staff_role || "—"}</span>
                <span className="text-[11px] text-slate-400">{fmtDate(n.created_at)}</span>
              </div>
              <p className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-snug">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
